import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { portalAPI } from '../../services/api';
import { toast } from 'react-toastify';
import './Welcome.css';

const Welcome = () => {
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get('session');
    const [loading, setLoading] = useState(true);
    const [sessionData, setSessionData] = useState(null);

    useEffect(() => {
        if (sessionId) {
            fetchSessionStatus();
            const interval = setInterval(fetchSessionStatus, 30000); // refresh every 30s
            return () => clearInterval(interval);
        }
    }, [sessionId]);

    const fetchSessionStatus = async () => {
        try {
            const response = await portalAPI.getSessionStatus(sessionId);
            setSessionData(response.data.data);
        } catch (error) {
            console.error('Failed to fetch session status');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = async () => {
        if (!window.confirm('Are you sure you want to disconnect?')) return;
        try {
            setLoading(true);
            await portalAPI.logout(sessionId);
            toast.success('Logged out successfully');
            // Redirect to landing
            const { branchId, clientIP, deviceMAC } = sessionData;
            window.location.href = `/landing?branchId=${branchId}&clientIP=${clientIP}&deviceMAC=${deviceMAC}`;
        } catch (error) {
            toast.error('Logout failed');
        } finally {
            setLoading(false);
        }
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const formatDuration = (ms) => {
        if (!ms) return '0m';
        const minutes = Math.floor(ms / 60000);
        const hours = Math.floor(minutes / 60);
        if (hours > 0) return `${hours}h ${minutes % 60}m`;
        return `${minutes}m`;
    };

    if (loading && !sessionData) {
        return (
            <div className="welcome-loading">
                <div className="spinner"></div>
                <p>Authenticating session...</p>
            </div>
        );
    }

    if (!sessionData || sessionData.status !== 'active') {
        return (
            <div className="welcome-container error">
                <div className="error-card card text-center p-8">
                    <i className="fas fa-unlink fa-4x mb-4 text-muted"></i>
                    <h2>Session Expired</h2>
                    <p>Your session has ended or is no longer active.</p>
                    <button className="btn btn-primary mt-6" onClick={() => window.location.href = '/landing'}>
                        LOGIN AGAIN
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="welcome-container">
            <div className="status-card card">
                <div className="status-header">
                    <div className="live-indicator">
                        <div className="pulse-dot"></div>
                        <span>CONNECTED</span>
                    </div>
                    <h1>Welcome, {sessionData.user?.name || 'Guest'}</h1>
                    <p>You are now online</p>
                </div>

                <div className="stats-grid">
                    <div className="stat-item">
                        <label>Plan</label>
                        <p>{sessionData.plan?.name}</p>
                    </div>
                    <div className="stat-item">
                        <label>Time Remaining</label>
                        <p className="highlight">{formatDuration(sessionData.timeRemaining)}</p>
                    </div>
                    <div className="stat-item">
                        <label>Data Usage</label>
                        <p>{formatBytes(sessionData.dataUsed)} / {formatBytes(sessionData.dataLimit)}</p>
                    </div>
                    <div className="stat-item">
                        <label>Device</label>
                        <p><code>{sessionData.deviceMAC}</code></p>
                    </div>
                </div>

                <div className="usage-progress">
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${(sessionData.dataUsed / sessionData.dataLimit) * 100}%` }}
                        ></div>
                    </div>
                    <p>{Math.round((sessionData.dataUsed / sessionData.dataLimit) * 100)}% of data used</p>
                </div>

                <div className="welcome-actions">
                    <button className="btn btn-outline w-full" onClick={handleLogout} disabled={loading}>
                        DISCONNECT
                    </button>
                    <p className="help-text">
                        Need help? Call support at <a href="tel:+1234567890">#1234</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Welcome;
